<template>
  <div class="observer-row" :class="{ 'observer-row--first': index === 0 }">
    <div v-if="index === 0" class="observer-header-user">
      {{ i18n.t('labels.user') }}
    </div>
    <div v-if="index === 0" class="observer-header-admin">
      {{ i18n.t('labels.admin') }}
    </div>

    <div class="observer-email">
      <EmailSuggestion
        :id="observer.key"
        v-model="observerData.email"
        data-test-id="observer-email"
        :enable-groups="true"
        :error="observerData.errorState"
        :label="i18n.t('labels.email')"
        :rules="[ruleEmail]"
        :signee="observerData"
        @group-members-selected="onGroupMembersSelected"
        @update:model-value="updateObserver"
      />
      <div
        v-if="observerData.errorMessage"
        class="text-error text-body-small mt-1"
      >
        {{ observerData.errorMessage }}
      </div>
    </div>

    <div class="observer-actions">
      <v-switch
        v-model="observerData.isAdmin"
        class="ml-3 mt-0 v-size--small"
        :class="{ active: observerData.isAdmin }"
        data-test-id="observer-is-admin"
        hide-details
        inset
        @keydown.enter="handleEnter"
        @update:model-value="updateObserver"
      />
      <v-btn
        data-test-id="observer-cancel"
        icon
        size="small"
        @click="removeObserver"
        @keydown.enter="removeObserver"
        @keydown.space="removeObserver"
      >
        <v-icon> fa fa-close </v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import EmailSuggestion from '@/components/inputs/Emailsuggestion/EmailSuggestion.vue';
import { computed, type PropType, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Observer } from '@/types/Observer';
import { ruleEmail } from '@deepcloud/field-validators';

const props = defineProps({
  index: { type: Number, required: true },
  observer: {
    type: Object as PropType<Observer>,
    required: true
  },
  observers: { type: Array as PropType<Observer[]>, required: true }
});

const i18n = useI18n();
const emit = defineEmits([
  'remove-observer',
  'update-observer',
  'add-group-observers'
]);

const observerData = ref({ ...props.observer });

const isEmailDuplicate = computed(() => {
  const email = observerData.value.email;
  return (
    email &&
    props.observers.filter(
      (obs) => obs.email?.toLowerCase() === email?.toLowerCase()
    ).length > 1
  );
});

const errorMessage = computed(() => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const email = observerData.value.email || '';
  if (email === '') {
    return null;
  } else if (isEmailDuplicate.value) {
    return 'Duplicate';
  } else if (!emailRegex.test(email)) {
    return 'E-mail must be valid';
  } else {
    return null;
  }
});

function removeObserver() {
  emit('remove-observer', props.index);
}

function handleEnter() {
  emit('update-observer', {
    observer: observerData.value,
    error: observerData.value.errorState
  });
  observerData.value.isAdmin = !observerData.value.isAdmin;
}

function updateObserver() {
  emit('update-observer', {
    observer: observerData.value,
    error: observerData.value.errorState
  });
}

// ─── Group members handling ─────────────────────────────────────────
// Emit the list of emails upward so ObserverForm can create new observer entries.
function onGroupMembersSelected(emails: string[]): void {
  emit(
    'add-group-observers',
    emails,
    props.observer.key,
    observerData.value.isAdmin
  );
}

watchEffect(() => {
  const error = errorMessage.value;
  observerData.value.errorState = !!error;
  observerData.value.errorMessage = error ?? '';
  emit('update-observer', {
    observer: observerData.value,
    error: observerData.value.errorState
  });
});
</script>

<style scoped lang="scss">
.v-switch.active :deep(.v-switch__track) {
  background-color: color-mix(
    in srgb,
    rgb(var(--v-theme-primary)) 25%,
    white
  ) !important;
}

.v-switch.active :deep(.v-switch__thumb) {
  background-color: rgb(var(--v-theme-primary)) !important;
}

.observer-row {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: start;
  gap: 0 8px;
  margin-top: 8px;
}

.observer-header-user {
  grid-column: 1;
  margin-bottom: 8px;
}

.observer-header-admin {
  grid-column: 2;
  margin-bottom: 8px;
  margin-left: 16px;
}

.observer-email {
  grid-column: 1;
  min-width: 0;
}

.observer-actions {
  grid-column: 2;
  display: flex;
  align-items: center;
  // Align with the input field height, not the error message
  height: 56px;
}

.error-slot {
  height: 20px;
  display: flex;
  align-items: center;
}
</style>
